import React, {useEffect, useRef, useState} from "react";
import {useNavigate, useOutletContext, useParams, useSearchParams} from "react-router-dom";
import Button from "../../../../../components/Button";
import api from "../../../../../lib/api";
import {notify} from "../../../../../components/Notifications";
import {getErrMsg} from "../../../../../lib/errors";
import {useBusiness} from "../../../../../context/BusinessContext";
import {MailIcon, SaveIcon} from "lucide-react";
import {MarkdownDisplay} from "../../../../../components/MarkdownDisplay";
import SectionHeading from "../../../../../components/SectionHeading";

type OutletCtx = { eventId: string | null };

type MailingForm = {
    confirmationEmailText: string;
    complementaryTicketEmailText: string;
    invoiceEmailText: string;
};

type MailingTab = "confirmation" | "complimentary" | "invoice";

const TABS: Array<{ key: MailingTab; label: string }> = [
    {key: "confirmation", label: "Confirmation"},
    {key: "complimentary", label: "Complimentary"},
    {key: "invoice", label: "Invoice"},
];

const cx = (...c: Array<string | false | null | undefined>) => c.filter(Boolean).join(" ");

function FieldLabel({title, subtitle}: { title: string; subtitle?: string }) {
    return (
        <div className="space-y-1">
            <div className="text-sm font-medium text-gray-900">{title}</div>
            {subtitle ? <div className="text-xs text-gray-500">{subtitle}</div> : null}
        </div>
    );
}

function Card({
                  title,
                  hint,
                  children,
                  right,
              }: {
    title: string;
    hint?: string;
    children: React.ReactNode;
    right?: React.ReactNode;
}) {
    return (
        <div className="rounded-2xl border border-gray-200 bg-white shadow-sm overflow-hidden">
            <div className="flex items-start justify-between gap-6 border-b border-gray-200 bg-gray-50 px-5 py-4">
                <div className="min-w-0">
                    <div className="flex items-center gap-2">
                        <MailIcon className="size-4 text-gray-500"/>
                        <h3 className="text-sm font-semibold text-gray-900">{title}</h3>
                    </div>
                    {hint ? <p className="mt-1 text-xs text-gray-500">{hint}</p> : null}
                </div>
                {right}
            </div>
            <div className="p-5">{children}</div>
        </div>
    );
}

function defaultConfirmation() {
    return "Thanks for your purchase! Your order is confirmed 🎉";
}

function defaultComplimentary() {
    return "🎟️ Your complimentary ticket is ready. Please find it attached below.";
}

function defaultInvoice() {
    return "Thanks for your order! Your invoice is below 🎉";
}

function insertAtCursor(
    textarea: HTMLTextAreaElement | null,
    text: string,
    currentValue: string,
    onChange: (next: string) => void
) {
    if (!textarea) {
        onChange((currentValue + (currentValue.endsWith(" ") ? "" : " ") + text).trimStart());
        return;
    }

    const start = textarea.selectionStart ?? currentValue.length;
    const end = textarea.selectionEnd ?? currentValue.length;

    const next = currentValue.slice(0, start) + text + currentValue.slice(end);
    onChange(next);

    // restore cursor
    requestAnimationFrame(() => {
        textarea.focus();
        const pos = start + text.length;
        textarea.setSelectionRange(pos, pos);
    });
}

export function MailingStep({mode}: { mode: "create" | "edit" }) {
    const {activeBusiness} = useBusiness();
    const businessId = activeBusiness?.id ?? null;

    const navigate = useNavigate();
    const params = useParams();
    const [sp] = useSearchParams();
    const outlet = useOutletContext<OutletCtx>();

    const eventId =
        mode === "edit"
            ? (params.eventId ?? outlet.eventId ?? null)
            : (outlet.eventId ?? sp.get("eventId") ?? null);

    const [loading, setLoading] = useState(false);
    const [saving, setSaving] = useState(false);

    const [form, setForm] = useState<MailingForm>({
        confirmationEmailText: "",
        complementaryTicketEmailText: "",
        invoiceEmailText: "",
    });

    const [activeTab, setActiveTab] = useState<MailingTab>("confirmation");

    const confirmRef = useRef<HTMLTextAreaElement>(null);
    const compRef = useRef<HTMLTextAreaElement>(null);
    const invoiceRef = useRef<HTMLTextAreaElement>(null);

    const baselineRef = useRef<MailingForm | null>(null);

    useEffect(() => {
        if (!businessId || !eventId) return;

        setLoading(true);
        (async () => {
            try {
                const {data} = await api.get(`/api/v1/business/${businessId}/events/${eventId}/mailing`);
                const next: MailingForm = {
                    confirmationEmailText: data?.confirmationMessageMarkdown ?? data?.confirmationEmailText ?? "",
                    complementaryTicketEmailText: data?.complementaryTicketMessageMarkdown ?? data?.complementaryTicketEmailText ?? "",
                    invoiceEmailText: data?.invoiceEmailText ?? "",
                };
                setForm(next);
                baselineRef.current = next;
            } catch (e) {
                notify.error(getErrMsg(e, "Failed to load mailing configuration"));
            } finally {
                setLoading(false);
            }
        })();
    }, [businessId, eventId]);

    const save = async () => {
        if (!businessId || !eventId) return;
        setSaving(true);
        try {
            const payload = {
                confirmationEmailText: form.confirmationEmailText,
                complementaryTicketEmailText: form.complementaryTicketEmailText,
                invoiceEmailText: form.invoiceEmailText,
            };

            await api.patch(`/api/v1/business/${businessId}/events/${eventId}/mailing`, payload);

            baselineRef.current = {...form};
            notify.success("Mailing saved");
        } catch (e) {
            notify.error(getErrMsg(e, "Failed to save mailing configuration"));
        } finally {
            setSaving(false);
        }
    };

    const goNext = () => {
        if (!eventId) return;
        if (mode === "edit") navigate(`/business/events/${encodeURIComponent(eventId)}/edit/reminders`);
        else navigate(`/business/events/new/reminders?eventId=${encodeURIComponent(eventId)}`);
    };

    const activeField: keyof MailingForm =
        activeTab === "confirmation"
            ? "confirmationEmailText"
            : activeTab === "complimentary"
                ? "complementaryTicketEmailText"
                : "invoiceEmailText";

    const activeDefault =
        activeTab === "confirmation"
            ? defaultConfirmation()
            : activeTab === "complimentary"
                ? defaultComplimentary()
                : defaultInvoice();

    const activeTitle =
        activeTab === "confirmation"
            ? "Confirmation message"
            : activeTab === "complimentary"
                ? "Complimentary ticket message"
                : "Invoice message";

    const activeSubtitle =
        activeTab === "confirmation"
            ? "Replaces the default “Thanks for your purchase! …” line in the email."
            : activeTab === "complimentary"
                ? "Shown in the complimentary ticket email."
                : "Shown in the initial invoice email only. Reminders and the expiry notice are not affected.";

    const activeRef =
        activeTab === "confirmation" ? confirmRef : activeTab === "complimentary" ? compRef : invoiceRef;

    const activeValue = form[activeField];

    const previewValue = activeValue?.trim() || activeDefault;

    if (!eventId) {
        return (
            <div className="rounded-xl border border-gray-200 bg-white p-6 text-sm text-gray-600">
                Please complete <span className="font-medium">Event Details</span> first.
            </div>
        );
    }

    return (
        <div className="space-y-6">

            <SectionHeading
                title={mode === "edit" ? "Edit mailing settings" : "Set up your mailing"}
                description={mode === "edit" ? "Customize the message blocks in your emails." : "Customize the message blocks in your emails. You can always change these later."}
                actions={
                    <>
                        <Button type="submit" variant="primary" size="md" onClick={save} disabled={saving}>
                            {saving ? "Saving…" :
                                <span className="inline-flex items-center gap-2"><SaveIcon
                                    className="size-4"/>Save</span>}
                        </Button>
                    </>
                }
            />

            {loading ? (
                <div className="py-10 text-sm text-gray-500">Loading mailing settings…</div>
            ) : (
                <div className="grid grid-cols-1 gap-6 lg:grid-cols-2">
                    {/* Editor */}
                    <Card
                        title="Email message blocks"
                        hint="These blocks support Markdown and replace the default highlighted message in the email."
                        right={
                            <div className="flex flex-wrap gap-2 rounded-lg bg-white p-1 ring-1 ring-gray-200">
                                {TABS.map((t) => (
                                    <button
                                        key={t.key}
                                        type="button"
                                        onClick={() => setActiveTab(t.key)}
                                        className={cx(
                                            "rounded-md px-3 py-1.5 text-xs font-semibold",
                                            activeTab === t.key ? "bg-gray-900 text-white" : "text-gray-700 hover:bg-gray-50"
                                        )}
                                    >
                                        {t.label}
                                    </button>
                                ))}
                            </div>
                        }
                    >
                        <div className="space-y-5">
                            <FieldLabel title={activeTitle} subtitle={activeSubtitle}/>

                            <textarea
                                ref={activeRef}
                                rows={10}
                                value={activeValue}
                                onChange={(e) => {
                                    const v = e.target.value;
                                    setForm((p) => ({...p, [activeField]: v}));
                                }}
                                placeholder={activeDefault}
                                className="w-full rounded-md border border-gray-300 px-3 py-2 text-sm focus:border-indigo-600 focus:outline-none"
                            />

                            <div
                                className="flex flex-wrap items-center justify-between gap-2 text-xs text-gray-500">
                                <div>
                                    Supports <span className="font-semibold">**bold**</span>, <span
                                    className="font-semibold">_italic_</span>, links, and lists.
                                </div>
                                <div>{activeValue.length} chars</div>
                            </div>
                        </div>
                    </Card>

                    {/* Preview */}
                    <Card
                        title="Live preview"
                        hint="This preview shows only the message block you are editing — not the full email template."
                    >
                        <div className="rounded-2xl border border-gray-200 bg-white p-5">
                            <div className="text-sm text-gray-700">Hi <span className="font-semibold">Customer</span>,
                            </div>

                            <div className="mt-2">
                                <MarkdownDisplay value={previewValue || "Nothing to preview"}/>
                            </div>

                            <div className="mt-6 rounded-xl border border-gray-200 p-4">
                                <div className="text-sm font-semibold text-gray-900">Event summary</div>
                                <div className="mt-2 text-sm text-gray-600">
                                    Tickets and order details will appear below in the email.
                                </div>
                            </div>
                        </div>

                        <div className="mt-4 text-xs text-gray-500">
                            Tip: keep it short. One or two lines looks best on mobile.
                        </div>
                    </Card>
                </div>
            )}

            <div className="flex items-center justify-end gap-3">
                <Button type="button" variant="secondary" size="md"
                        onClick={() => navigate("/business/events")}>
                    Exit
                </Button>
                <Button type="button" variant="primary" size="md" onClick={goNext} disabled={saving}>
                    Continue to Reminders
                </Button>
            </div>

        </div>
    );
}
